import { getPublicEnv } from "@/config/get-public-env";

/** The id of the permanent first tab of the bar at the very top: the main page of the site. It is never stored with the others and never closes. */
export const HOME_TAB_ID = "home";

/** One tab of the bar at the very top: its own id, the app it shows and the address of the page it is on (it comes back there when chosen). */
type AppTab = { id: string; appId: string; href: string };

/** The tabs of the bar at the very top and which one is the current one. */
export type AppTabsState = { tabs: AppTab[]; active: string };

/** The tabs kept in the browser's session storage under `key`, or null when there are none or what is there is not tabs (so a damaged value never breaks the page). The current tab is the home tab or one of the stored ones. */
function readAppTabs(key: string): AppTabsState | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(key) ?? "null") as AppTabsState | null;
    const valid = value && typeof value.active === "string" && Array.isArray(value.tabs) && value.tabs.every((t) => typeof t?.id === "string" && typeof t.appId === "string" && typeof t.href === "string");
    return valid && (value.active === HOME_TAB_ID || value.tabs.some((t) => t.id === value.active)) ? value : null;
  } catch {
    return null;
  }
}

/** Keeps the tabs in the browser's session storage under `key` (nothing happens when the storage is unavailable: the tabs just do not survive a reload). */
function writeAppTabs(key: string, state: AppTabsState): void {
  try { window.sessionStorage.setItem(key, JSON.stringify(state)); } catch { /* storage unavailable: the tabs work for this page only */ }
}

type Store = { get: () => AppTabsState; set: (next: AppTabsState) => void; subscribe: (listener: () => void) => () => void };

const stores = new Map<string, Store>();

const EMPTY: AppTabsState = { tabs: [], active: HOME_TAB_ID };

/** The store of the tabs kept under `key`, outside React (readable on the client without a mismatch with the server's render): read from session storage the first time (no tabs, with the home tab current, when there is nothing), the same value until it changes, saved on every change.
 *  One store per key, shared by everything that asks (the bar, the app switcher, the links to apps). */
export function appTabsStore(key: string): Store {
  const known = stores.get(key);
  if (known) return known;
  let state: AppTabsState = EMPTY;
  let loaded = false;
  const listeners = new Set<() => void>();
  const store: Store = {
    get: () => { if (!loaded) { state = readAppTabs(key) ?? EMPTY; loaded = true; } return state; },
    set: (next) => { state = next; loaded = true; writeAppTabs(key, next); listeners.forEach((l) => l()); },
    subscribe: (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
  };
  stores.set(key, store);
  return store;
}

/** The session-storage name of the tabs of the bar at the very top (the shared prefix from .env plus `apps`). */
export function appTabsKey(): string {
  return `${getPublicEnv().NEXT_PUBLIC_TABS_STORAGE_PREFIX}apps`;
}

/** Opens a new tab on an app's home page (`id` is made by the caller) and makes it the current one. */
export function openAppTab(state: AppTabsState, id: string, appId: string, href: string): AppTabsState {
  return { tabs: [...state.tabs, { id, appId, href }], active: id };
}

/** Closes a tab (the home tab is not one of them and never closes). When the current tab closes the one before it becomes current, else the one after it, else the home tab. */
export function closeAppTab(state: AppTabsState, id: string): AppTabsState {
  const at = state.tabs.findIndex((t) => t.id === id);
  if (at < 0) return state;
  const tabs = state.tabs.filter((t) => t.id !== id);
  return { tabs, active: state.active === id ? (tabs[at - 1] ?? tabs[at])?.id ?? HOME_TAB_ID : state.active };
}

/** Closes the tabs of apps the person can no longer open (for example a module switched off in the App store); the same state comes back when there is nothing to close. */
export function pruneAppTabs(state: AppTabsState, appIds: readonly string[]): AppTabsState {
  return state.tabs.filter((t) => !appIds.includes(t.appId)).reduce((next, t) => closeAppTab(next, t.id), state);
}

/** Makes the tabs agree with the page now shown, whatever brought the person there (a tab, the app switcher, a link, a typed address): the main page makes the permanent home tab current; a page of an app makes that app's tab current, using the current tab when it is of that app, else the first tab of the app,
 *  else a new tab (`newId`); and that tab remembers the page's address (`href`). A page of no app changes nothing. */
export function syncAppTabs(state: AppTabsState, page: { appId: string | null; home: boolean; href: string }, newId: string): AppTabsState {
  if (page.home) return state.active === HOME_TAB_ID ? state : { ...state, active: HOME_TAB_ID };
  if (!page.appId) return state;
  const current = state.tabs.find((t) => t.id === state.active && t.appId === page.appId) ?? state.tabs.find((t) => t.appId === page.appId);
  if (!current) return { tabs: [...state.tabs, { id: newId, appId: page.appId, href: page.href }], active: newId };
  if (current.id === state.active && current.href === page.href) return state;
  return { active: current.id, tabs: state.tabs.map((t) => (t.id === current.id ? { ...t, href: page.href } : t)) };
}

/** The app a page belongs to, from its address without the language (`/support/t/abc` is `support`): the app whose home address starts it, the longest first; null for a page of no app. `apps` are the apps as `{ id, href }`. */
export function appOfPath(path: string, apps: readonly { id: string; href: string }[]): string | null {
  const found = [...apps].sort((a, b) => b.href.length - a.href.length).find((a) => path === a.href || path.startsWith(`${a.href}/`));
  return found?.id ?? null;
}

/** Goes to an app through the tabs: when a tab of that app is already open it becomes current (the current one first, else the first) and its page is where the person goes back to; otherwise a new tab opens on the app's home page (`href`, with `newId`). Gives the new state and the address to go to. */
export function openApp(state: AppTabsState, appId: string, href: string, newId: string): { state: AppTabsState; go: string } {
  const open = state.tabs.find((t) => t.id === state.active && t.appId === appId) ?? state.tabs.find((t) => t.appId === appId);
  if (open) return { state: { ...state, active: open.id }, go: open.href };
  return { state: { tabs: [...state.tabs, { id: newId, appId, href }], active: newId }, go: href };
}
